import { jsx as _jsx } from "react/jsx-runtime";
import * as Sentry from '@sentry/react';
import { jwtDecode } from 'jwt-decode';
import { createContext, useContext, useEffect, useState } from 'react';
import { useDeviceId } from '../hooks/use-device-id.hook';
import { useSettings } from './SettingsContext';
export const AuthContext = createContext(undefined);
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
export const getAuthorizationToken = () => {
    return localStorage.getItem('token');
};
export const AuthProvider = ({ children }) => {
    const [loggedIn, setLoggedIn] = useState(false);
    const [identity, setIdentity] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const { apiUrl } = useSettings();
    const deviceId = useDeviceId();
    const identityId = identity?.id || null;
    const refreshIdentity = () => {
        const authorizationToken = getAuthorizationToken();
        if (authorizationToken == null) {
            setLoggedIn(false);
            setIdentity(null);
            Sentry.setUser(null);
            return false;
        }
        else {
            try {
                const decodedToken = jwtDecode(authorizationToken);
                const currentTime = new Date().getTime() / 1000;
                if (currentTime > decodedToken.exp) {
                    setLoggedIn(false);
                    setIdentity(null);
                    Sentry.setUser(null);
                    return false;
                }
                const identityData = decodedToken.identity;
                setLoggedIn(true);
                setIdentity(identityData);
                Sentry.setUser({
                    id: identityData.id,
                    username: identityData.username
                });
                return true;
            }
            catch (error) {
                console.error('Error decoding token:', error);
                setLoggedIn(false);
                setIdentity(null);
                Sentry.setUser(null);
                return false;
            }
        }
    };
    const login = async (credentials) => {
        setIsLoading(true);
        try {
            const response = await fetch(apiUrl + '/auth/login', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ ...credentials, deviceId: credentials.deviceId ?? deviceId })
            });
            const data = await response.json();
            if (data.token) {
                setAuthorizationToken(data.token);
            }
            setIsLoading(false);
            return data;
        }
        catch (error) {
            setIsLoading(false);
            console.error('Login failed:', error);
            throw error;
        }
    };
    const logout = () => {
        setAuthorizationToken(null);
    };
    const setAuthorizationToken = (token) => {
        if (token == null) {
            localStorage.removeItem('token');
        }
        else {
            localStorage.setItem('token', token);
        }
        refreshIdentity();
    };
    const checkAuthorized = () => {
        return refreshIdentity();
    };
    useEffect(() => {
        const startup = async () => {
            refreshIdentity();
            setIsLoading(false);
            setIsInitialized(true);
        };
        startup();
    }, []);
    const value = {
        loggedIn,
        identity,
        identityId,
        login,
        logout,
        setAuthorizationToken,
        getAuthorizationToken,
        checkAuthorized,
        isLoading,
        isInitialized
    };
    return _jsx(AuthContext.Provider, { value: value, children: children });
};
//# sourceMappingURL=AuthContext.js.map